import React from 'react';
import { Space, Spin, Empty, Typography } from 'antd';
import { PaperClipOutlined, ClockCircleOutlined } from '@ant-design/icons';
import config from 'config';

const { Text, Paragraph } = Typography;

export interface AssessmentReportReplyAttachment {
    id: string;
    fileName: string;
    contentType: string | null;
    sizeBytes: number | null;
    url: string;
}

export interface AssessmentReportReply {
    id: string;
    fromAddress: string;
    subject: string;
    bodyText: string | null;
    bodyHtml: string | null;
    receivedAt: string;
    attachments: AssessmentReportReplyAttachment[];
}

export const WaitingForReplyState: React.FC<{ message: string }> = ({ message }) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            height: '100%',
            minHeight: 260,
            padding: 24,
        }}
    >
        <style>{`
            @keyframes waitingForReplyPulse {
                0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 91, 92, 0.25); }
                50% { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(255, 91, 92, 0); }
            }
        `}</style>
        <div
            style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fff1f0, #ffd9d9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
                animation: 'waitingForReplyPulse 2s ease-in-out infinite',
            }}
        >
            <ClockCircleOutlined style={{ fontSize: 28, color: '#ff5b5c' }} />
        </div>
        <div style={{ fontSize: 15, lineHeight: 1.6, color: '#4a5568', maxWidth: 320 }}>
            {message}
        </div>
    </div>
);

interface AssessmentReportRepliesListProps {
    loading: boolean;
    error: boolean;
    replies: AssessmentReportReply[];
    loadErrorMessage: string;
    emptyMessage: string;
}

export const AssessmentReportRepliesList: React.FC<AssessmentReportRepliesListProps> = ({
    loading,
    error,
    replies,
    loadErrorMessage,
    emptyMessage,
}) => {
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return <Empty description={loadErrorMessage} />;
    }

    if (replies.length === 0) {
        return <WaitingForReplyState message={emptyMessage} />;
    }

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {replies.map(reply => (
                <div
                    key={reply.id}
                    style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, background: '#fff' }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                        <div>
                            <Text strong>{reply.subject}</Text>
                            <br />
                            <Text type="secondary">{reply.fromAddress}</Text>
                        </div>
                        <Text type="secondary">
                            {new Date(reply.receivedAt).toLocaleString(undefined, {
                                year: 'numeric', month: '2-digit', day: '2-digit',
                                hour: '2-digit', minute: '2-digit',
                            })}
                        </Text>
                    </div>

                    {reply.bodyHtml ? (
                        <iframe
                            title={reply.subject}
                            srcDoc={reply.bodyHtml}
                            sandbox=""
                            style={{ width: '100%', height: 260, border: '1px solid #f0f0f0', borderRadius: 4, background: '#fff' }}
                        />
                    ) : (
                        <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                            {reply.bodyText}
                        </Paragraph>
                    )}

                    {reply.attachments.length > 0 && (
                        <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
                            {reply.attachments.map(attachment => (
                                <a
                                    key={attachment.id}
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <PaperClipOutlined /> {attachment.fileName}
                                </a>
                            ))}
                        </Space>
                    )}
                </div>
            ))}
        </Space>
    );
};

export async function fetchAssessmentReportReplies(
    landingPageDocId: string,
    headers: Record<string, string>,
): Promise<AssessmentReportReply[]> {
    const response = await fetch(`${config.api.base}${config.api.landingPageAssessmentReports}/${landingPageDocId}/replies`, {
        headers,
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    const data: { replies: AssessmentReportReply[] } = await response.json();
    return data.replies;
}
